<template>
  <div
    class="heart-with-tooltip"
    :class="[{ 'is-active': isActive }, `heart-with-tooltip--${variant}`]"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
    @touchmove="handleTouchMove"
    @click.stop
    @mousedown.stop
  >
    <div class="heart-icon-wrapper">
      <FakeProfileHeartIcon />
    </div>
    <div class="tooltip-container">
      <div class="tooltip-content">
        <span class="tooltip-text">{{ trans.get('profile:index.profile_moderated') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import FakeProfileHeartIcon from './FakeProfileHeartIcon.vue';

export default {
  name: 'HeartWithTooltip',
  components: { FakeProfileHeartIcon },
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary'].includes(value),
    },
  },
  data() {
    return {
      isActive: false,
      touchTimeout: null,
      LONG_PRESS_DELAY: 200,
      TOOLTIP_HIDE_DELAY: 500,
    };
  },
  methods: {
    handleTouchStart(event) {
      if (this.variant !== 'secondary') return;

      event.stopPropagation();
      event.preventDefault();

      this.touchTimeout = setTimeout(() => {
        this.isActive = true;
      }, this.LONG_PRESS_DELAY);
    },
    handleTouchEnd(event) {
      if (this.variant !== 'secondary') return;

      event.stopPropagation();
      event.preventDefault();

      clearTimeout(this.touchTimeout);
      setTimeout(() => {
        this.isActive = false;
      }, this.TOOLTIP_HIDE_DELAY);
    },
    handleTouchMove(event) {
      if (this.variant !== 'secondary') return;

      event.stopPropagation();
    },
  },
  beforeDestroy() {
    if (this.touchTimeout) {
      clearTimeout(this.touchTimeout);
    }
  },
};
</script>

<style scoped lang="scss">
@import '../../../../sass/common/base/_base-mixin.scss';

.heart-with-tooltip {
  position: relative;
  display: inline-block;
  vertical-align: top;
  cursor: pointer;

  // Primary version styles
  &--primary {
    margin-left: -3px;
    margin-top: 3px;

    .heart-icon-wrapper {
      ::v-deep svg {
        color: #505050;
      }
    }

    // Hover effect for primary variant - shows tooltip with gradient background
    &:hover {
      .tooltip-content {
        background: linear-gradient(91.56deg, #c63e7c 9.1%, #c63e7c 93.29%);
        padding: 6px 10px 6px 17px;
      }

      .heart-icon-wrapper {
        ::v-deep svg {
          color: #fff;
        }
      }

      .tooltip-text {
        max-width: 200px;
        opacity: 1;
        transform: translateX(0);
        padding-left: 8px;
      }
    }
    @include tablet-down {
      display: none;
    }
  }

  // Secondary version styles
  &--secondary {
    order: 2;
    margin-left: 0;
    margin-top: 0;

    .heart-icon-wrapper {
      ::v-deep svg {
        color: #fff;
        height: 15px;
        width: auto;
      }
    }

    // Hover effect for devices that support hover (e.g., tablets with stylus, some hybrids)
    // CSS-only hover - no JavaScript needed for this interaction
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        .tooltip-content {
          background: linear-gradient(91.56deg, rgba(198, 62, 124, 0.3) 9.1%, rgba(198, 62, 124, 0.3) 93.29%);
          padding: 6px 10px 6px 28px;
          backdrop-filter: blur(2px);
          box-shadow: 0 8px 32px rgba(198, 62, 124, 0.2), inset 0 2px 10px rgba(255, 255, 255, 0.15);
        }

        .tooltip-text {
          max-width: 200px;
          opacity: 1;
          transform: translateX(0);
        }
      }
    }

    // Active state for long press on touch devices
    // Triggered by JavaScript long press detection
    &.is-active {
      .tooltip-content {
        background: linear-gradient(91.56deg, rgba(198, 62, 124, 0.3) 9.1%, rgba(198, 62, 124, 0.3) 93.29%);
        padding: 6px 10px 6px 28px;
        backdrop-filter: blur(2px);
        box-shadow: 0 8px 32px rgba(198, 62, 124, 0.2), inset 0 2px 10px rgba(255, 255, 255, 0.15);
      }

      .tooltip-text {
        max-width: 200px;
        opacity: 1;
        transform: translateX(0);
      }
    }
  }
}

.heart-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease-in-out;

  ::v-deep svg {
    display: block;
    width: 14px;
    height: auto;
    transition: color 0.4s ease;
  }
}

.tooltip-container {
  position: absolute;
  top: 49%;
  left: -5px;
  transform: translateY(-50%);
  z-index: 1;
  pointer-events: none;
}

.tooltip-content {
  display: flex;
  align-items: center;
  background: transparent;
  border-radius: 35px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  max-width: fit-content;
  padding: 6px 0 6px 0;
}

.tooltip-text {
  display: inline-block;
  font-size: 14px;
  font-weight: 300;
  font-style: italic;
  color: #fff;
  padding: 0;
  letter-spacing: 0.3px;
  max-width: 0;
  opacity: 0;
  white-space: nowrap;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(-10px);
  line-height: 1;
  user-select: none;
}
</style>
